import React, { useEffect, useState } from "react";
import { useError } from "../../../contexts/ErrorContext";
import useApi from "../../../hooks/useGaneshApi";

import { Box, Typography } from "@mui/material";
import EasyAnchorLink from "../../UI/AnchorLink";

import { GridColDef } from "@mui/x-data-grid-premium";
import { renderNumberCell } from "utils/dataGrid";
import GaneshDataGrid from "../../DataGrid/GaneshDataGrid";

import "../BrandMetadataReview.css";

interface PlacesOmniReviewProps {
  omni_key: string;
  country_code: string;
}

const ReviewPlacesOmniFillRates: React.FC<PlacesOmniReviewProps> = ({
  omni_key,
  country_code,
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any>([]);

  const { showError } = useError();
  const { apiRequest } = useApi();
  const columns_def: GridColDef[] = [
    {
      field: "chain_id",
      headerName: "Chain ID",
      description: "The chain ID.",
      type: "string",
      width: 200,
    },
    {
      field: "country",
      headerName: "Country",
      description: "The name of the country.",
      type: "string",
      width: 200,
    },
    {
      field: "country_code",
      headerName: "Country Code",
      description: "The country code for the associated data.",
      type: "string",
      width: 150,
    },
    {
      field: "active_count",
      headerName: "Active Count",
      description:
        "The total number of active places for the brand in the country.",
      type: "number",
      width: 150,
      renderCell: renderNumberCell,
    },
    {
      field: "poi_name_fill_rate",
      headerName: "Name Fill Rate",
      description: "The fill rate for the name attribute of places.",
      type: "number",
      width: 150,
      renderCell: renderNumberCell,
    },
    {
      field: "poi_address_fill_rate",
      headerName: "Address Fill Rate",
      description: "The fill rate for the address attribute of places.",
      type: "number",
      width: 150,
      renderCell: renderNumberCell,
    },
    {
      field: "poi_coordinates_fill_rate",
      headerName: "Coordinates Fill Rate",
      description: "The fill rate for the coordinates attribute of places.",
      type: "number",
      width: 150,
      renderCell: renderNumberCell,
    },
    {
      field: "poi_business_category_fill_rate",
      headerName: "Business Category Fill Rate",
      description:
        "The fill rate for the business category attribute of places.",
      type: "number",
      width: 200,
      renderCell: renderNumberCell,
    },
    {
      field: "poi_website_fill_rate",
      headerName: "Website Fill Rate",
      description: "The fill rate for the website attribute of places.",
      type: "number",
      width: 150,
      renderCell: renderNumberCell,
    },
    {
      field: "poi_building_polygon_fill_rate",
      headerName: "Building Polygon Fill Rate",
      description:
        "The fill rate for the building polygon attribute of places.",
      type: "number",
      width: 200,
      renderCell: renderNumberCell,
    },
  ];

  useEffect(() => {
    if (omni_key && country_code) {
      setLoading(true);
      const params = new URLSearchParams({
        omni_key: omni_key,
        country_code: country_code,
      });

      const fetchFillRates = async () => {
        try {
          const response = await apiRequest<any>(
            `/brand_metadata/places_fillrates_review?${params.toString()}`,
            {
              method: "GET",
            }
          );
          if (response.data) {
            setData(response.data);
          }
        } catch (error) {
          console.error(error);
          showError(
            `Failed to fetch places fill rates to review for omni ${omni_key} + ${country_code}.`
          );
        } finally {
          setLoading(false);
        }
      };
      fetchFillRates();
    } else {
      setData([]);
    }
  }, [omni_key, country_code]);

  return (
    <Box className="metadata-review-container">
      <Box display="flex" alignItems="center" id="omni-fill-rates">
        <EasyAnchorLink id="omni-fill-rates" />
        <Typography variant="h6" gutterBottom sx={{ fontSize: "1.25rem" }}>
          Fill Rates Review
        </Typography>
      </Box>

      <GaneshDataGrid
        gridId="placesReviewOmniFillRates"
        columns={columns_def}
        data={data}
        loading={loading}
        headerFilters={data.length > 0 ? true : false}
        customHeight={data.length > 0 ? "750px" : "300px"}
      />
    </Box>
  );
};

export default ReviewPlacesOmniFillRates;
